const CONSENT_SELECTOR = '[data-mml-cart-consent]';
const CHECKBOX_SELECTOR = '[data-mml-cart-consent-checkbox]';
const ERROR_SELECTOR = '[data-mml-cart-consent-error]';

let consentGranted = false;
let scheduled = false;
const observedRoots = new WeakSet();

function addStyles() {
  if (document.getElementById('mml-cart-consent-styles')) return;

  const style = document.createElement('style');
  style.id = 'mml-cart-consent-styles';
  style.textContent = `
    .mml-cart-consent {
      width: 100%;
      margin: 0 0 var(--gap-sm, 12px);
      color: var(--color-foreground, currentColor);
      font-size: var(--font-size--sm, 14px);
      line-height: 1.45;
    }

    .mml-cart-consent__label {
      display: grid;
      grid-template-columns: 20px minmax(0, 1fr);
      gap: 10px;
      align-items: start;
      cursor: pointer;
    }

    .mml-cart-consent__checkbox {
      width: 18px;
      height: 18px;
      margin: 2px 0 0;
      accent-color: var(--color-foreground, #111);
      cursor: pointer;
    }

    .mml-cart-consent__text { min-width: 0; }

    .mml-cart-consent__text a {
      color: inherit;
      font-weight: 600;
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    .mml-cart-consent__text a:hover { text-decoration-thickness: 2px; }

    .mml-cart-consent__error {
      margin: 6px 0 0 30px;
      font-size: var(--font-size--2xs, 12px);
      line-height: 1.4;
    }

    .mml-cart-consent__error[hidden] { display: none; }

    .mml-cart-consent--invalid .mml-cart-consent__label {
      outline: 2px solid currentColor;
      outline-offset: 5px;
      border-radius: 2px;
    }

    .mml-cart-consent-locked {
      opacity: 0.55;
      pointer-events: none;
    }

    .mml-legacy-consent-hidden { display: none !important; }

    .cart__checkout-button:disabled { cursor: not-allowed; }
  `;
  document.head.appendChild(style);
}

function normalizedText(element) {
  return (element?.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function hasLegalLinks(element) {
  if (!element) return false;
  const links = [...element.querySelectorAll('a[href]')];
  const hasAgb = links.some((link) => link.getAttribute('href')?.includes('/pages/agb'));
  const hasPrivacy = links.some((link) => link.getAttribute('href')?.includes('/pages/datenschutz'));
  return hasAgb && hasPrivacy;
}

function findLegacyConsentRow(scope) {
  const legalLinks = [...scope.querySelectorAll('a[href*="/pages/agb"], a[href*="/pages/datenschutz"]')].filter(
    (link) => !link.closest(CONSENT_SELECTOR),
  );

  for (const link of legalLinks) {
    let node = link.parentElement;

    for (let depth = 0; node && depth < 7; depth += 1, node = node.parentElement) {
      if (node.closest(CONSENT_SELECTOR)) break;
      if (node.matches?.('.cart__ctas, .cart-totals, .cart-drawer__summary, .cart-summary, cart-drawer-component')) break;

      const checkboxes = [...node.querySelectorAll('input[type="checkbox"]')].filter(
        (checkbox) => !checkbox.closest(CONSENT_SELECTOR),
      );

      if (checkboxes.length !== 1 || !hasLegalLinks(node)) continue;
      if (node.querySelector('.cart__checkout-button, .cart-totals__total, [data-cart-subtotal]')) continue;
      if (normalizedText(node).length > 260) continue;

      return { checkbox: checkboxes[0], row: node };
    }
  }

  return null;
}

function getLegacyConsentEntries() {
  const entries = [];
  const seen = new Set();

  document.querySelectorAll('.cart__ctas').forEach((ctas) => {
    const scope = ctas.parentElement || ctas;
    const entry = findLegacyConsentRow(scope);
    if (!entry || seen.has(entry.checkbox)) return;
    seen.add(entry.checkbox);
    entries.push(entry);
  });

  return entries;
}

function hideLegacyConsent() {
  getLegacyConsentEntries().forEach(({ row }) => {
    row.classList.add('mml-legacy-consent-hidden');
    row.setAttribute('aria-hidden', 'true');
  });
}

function syncLegacyConsent() {
  getLegacyConsentEntries().forEach(({ checkbox }) => {
    if (checkbox.checked === consentGranted) return;
    checkbox.click();
  });
}

function createConsent(index) {
  const wrapper = document.createElement('div');
  const checkboxId = `mml-cart-consent-${index}`;

  wrapper.className = 'mml-cart-consent cart-primary-typography';
  wrapper.dataset.mmlCartConsent = '';
  wrapper.innerHTML = `
    <label class="mml-cart-consent__label" for="${checkboxId}">
      <input
        id="${checkboxId}"
        class="mml-cart-consent__checkbox"
        type="checkbox"
        data-mml-cart-consent-checkbox
        ${consentGranted ? 'checked' : ''}
      >
      <span class="mml-cart-consent__text">
        Ich habe die <a href="/pages/agb">AGB</a> und die
        <a href="/pages/datenschutz">Datenschutzerklärung</a> zur Kenntnis genommen.
      </span>
    </label>
    <p class="mml-cart-consent__error" data-mml-cart-consent-error role="alert" hidden>
      Bitte bestätige die AGB und die Datenschutzerklärung, bevor du zur Kasse gehst.
    </p>
  `;

  wrapper.querySelector(CHECKBOX_SELECTOR)?.addEventListener('change', (event) => {
    consentGranted = event.currentTarget.checked;
    syncLegacyConsent();
    updateAllControls();
  });

  return wrapper;
}

function setCheckoutState(scope) {
  const checkoutButton = scope.querySelector('.cart__checkout-button');
  if (checkoutButton) {
    checkoutButton.disabled = !consentGranted;
    checkoutButton.setAttribute('aria-disabled', String(checkoutButton.disabled));
  }

  const accelerated = scope.querySelector('.additional-checkout-buttons');
  if (accelerated) {
    accelerated.classList.toggle('mml-cart-consent-locked', !consentGranted);
    if (!consentGranted) {
      accelerated.setAttribute('inert', '');
      accelerated.setAttribute('aria-disabled', 'true');
    } else {
      accelerated.removeAttribute('inert');
      accelerated.removeAttribute('aria-disabled');
    }
  }
}

function updateAllControls() {
  document.querySelectorAll(CONSENT_SELECTOR).forEach((wrapper) => {
    const checkbox = wrapper.querySelector(CHECKBOX_SELECTOR);
    const error = wrapper.querySelector(ERROR_SELECTOR);

    if (checkbox && checkbox.checked !== consentGranted) checkbox.checked = consentGranted;
    wrapper.classList.remove('mml-cart-consent--invalid');
    if (error) error.hidden = true;
  });

  document.querySelectorAll('.cart__ctas').forEach((ctas) => {
    const scope = ctas.parentElement || ctas;
    setCheckoutState(scope);
  });
}

function showConsentError(ctas) {
  const scope = ctas?.parentElement || document;
  const wrapper = scope.querySelector(CONSENT_SELECTOR) || document.querySelector(CONSENT_SELECTOR);
  if (!wrapper) return;

  wrapper.classList.add('mml-cart-consent--invalid');
  const error = wrapper.querySelector(ERROR_SELECTOR);
  if (error) error.hidden = false;
  wrapper.querySelector(CHECKBOX_SELECTOR)?.focus({ preventScroll: true });
  wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function installConsent() {
  const ctasList = [...document.querySelectorAll('.cart__ctas')];
  if (!ctasList.length) return;

  addStyles();

  ctasList.forEach((ctas, index) => {
    const scope = ctas.parentElement || ctas;
    let consent = scope.querySelector(CONSENT_SELECTOR);

    if (!consent) {
      consent = createConsent(index);
      ctas.before(consent);
    }

    setCheckoutState(scope);
  });

  hideLegacyConsent();
}

function scheduleInstall() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    installConsent();
  });
}

function observeCartRoot(root) {
  if (!root || observedRoots.has(root)) return;

  observedRoots.add(root);
  const observer = new MutationObserver(scheduleInstall);
  observer.observe(root, { childList: true, subtree: true });
}

function installCartObservers() {
  observeCartRoot(document.getElementById('shopify-section-cart-drawer-section'));

  const cartMain = document.querySelector('#MainContent[data-template^="cart"]');
  observeCartRoot(cartMain);
}

function initConsent() {
  installConsent();
  installCartObservers();
}

document.addEventListener(
  'click',
  (event) => {
    if (consentGranted) return;

    const checkoutButton = event.target.closest('.cart__checkout-button');
    const accelerated = event.target.closest('.additional-checkout-buttons');
    if (!checkoutButton && !accelerated) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    showConsentError((checkoutButton || accelerated).closest('.cart__ctas'));
  },
  true,
);

document.addEventListener(
  'submit',
  (event) => {
    if (consentGranted) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.id !== 'cart-form' && !form.matches('form[action*="/cart"]')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    showConsentError(form.querySelector('.cart__ctas') || document.querySelector('.cart__ctas'));
  },
  true,
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initConsent, { once: true });
} else {
  initConsent();
}
